import React, { ButtonHTMLAttributes } from 'react';
import classNames from 'classnames';
import { IHasTestData } from '../../ui-toolkit/test-utilities/IHasTestData';
import { mergeTests } from '../../ui-toolkit/test-utilities/mergeTests';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

interface ButtonTWProps extends ButtonHTMLAttributes<HTMLButtonElement>, IHasTestData {
  secondary?: boolean;
  ghost?: boolean;
  error?: boolean;
  children?: React.ReactNode;
}

export const ButtonTWClasses = ({
  secondary,
  ghost,
  error,
  useSpruceColors,
}: {
  secondary?: boolean;
  ghost?: boolean;
  error?: boolean;
  useSpruceColors?: boolean;
}) => {
  const secondaryClassNames = useSpruceColors
    ? 'bg-white text-[#0A4A43] disabled:bg-zinc-100 disabled:text-zinc-500 disabled:ring-0'
    : 'bg-white text-foundation-primary disabled:bg-zinc-100 disabled:text-zinc-500 disabled:ring-0';
  const notGhostClassNames = useSpruceColors
    ? 'ring-1 ring-inset ring-[#0A4A43] hover:bg-gray-50'
    : 'ring-1 ring-inset ring-foundation-primary hover:bg-extra-grey-light';
  const primaryClassNames = useSpruceColors
    ? 'bg-[#0A4A43] hover:bg-[#083A35] text-white disabled:bg-zinc-100 disabled:text-zinc-500'
    : 'bg-foundation-primary hover:bg-extension-foundation-primary-darkest text-white disabled:bg-zinc-100 disabled:text-zinc-500';
  const errorClassNames = useSpruceColors
    ? 'bg-red-100 text-[#0A4A43] disabled:bg-zinc-100 disabled:text-zinc-500 disabled:ring-0 ring-1 ring-inset ring-foundation-error hover:bg-red-200'
    : 'bg-red-100 text-foundation-primary disabled:bg-zinc-100 disabled:text-zinc-500 disabled:ring-0 ring-1 ring-inset ring-foundation-error hover:bg-red-200';

  switch (true) {
    case ghost && secondary:
      return classNames(secondaryClassNames);
    case secondary:
      return classNames(secondaryClassNames, notGhostClassNames);
    case error:
      return classNames(errorClassNames);
    default:
      return classNames(primaryClassNames);
  }
};
export const ButtonTW: React.FC<ButtonTWProps> = ({ children, className, secondary, ghost, error, ...rest }) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const useSpruceColors = !isZillowClosingEnabled;
  const background = ButtonTWClasses({ ghost, secondary, error, useSpruceColors });

  return (
    <ColorlessButtonTW className={classNames(background, className)} {...rest}>
      {children}
    </ColorlessButtonTW>
  );
};

export const ColorlessButtonClasses =
  'rounded-full py-1.5 px-3 text-sm font-normal shadow-sm focus:ring-foundation-primary';
// The developer **must** inject TW styles for colors
export const ColorlessButtonTW: React.FC<ButtonHTMLAttributes<HTMLButtonElement> & IHasTestData> = ({
  className,
  children,
  ...rest
}) => {
  return (
    <button
      type="button"
      className={classNames(ColorlessButtonClasses, className)}
      data-test-id={mergeTests(rest['data-test-id'], 'button-tw')}
      data-test-class={mergeTests(rest['data-test-id'], 'button-tw')}
      {...rest}
    >
      {children}
    </button>
  );
};
